import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookmarkIcon, FolderIcon, Edit2Icon, TrashIcon, MoveIcon, SearchIcon, Bell, FolderPlus, X, AlertCircle, MapPin, DollarSign } from 'lucide-react';

// Import UI components
import UnifiedButton from '../../../components/unified/UnifiedButton';
import UnifiedInput from '../../../components/unified/UnifiedInput';
import UnifiedCard from '../../../components/unified/UnifiedCard';
import Chip from '../../../components/ui/Chip';

// Import design tokens
import { typography, colors } from '../../../styles/design-tokens';

// Import services
import {
  getSavedJobs,
  unsaveJob,
  updateSavedJob,
  createFolder,
  getFolders,
  deleteFolder,
  getJobAlerts,
  markAlertAsRead
} from '../../../services/savedJobsService';

// Import auth context
import { useAuth } from '../../../contexts/AuthContext';

// Import search data for job details
import { searchData } from '../../../mocks/search';

// Import real-time subscriptions
import { useRealtimeSubscriptions } from '../../../hooks/useRealtimeSubscriptions';

const SavedJobs = () => {
  const navigate = useNavigate();
  const authContext = useAuth();
  const { user = null } = authContext || {};

  const [savedJobs, setSavedJobs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedFolder, setSelectedFolder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showEditNotesModal, setShowEditNotesModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const [folderForm, setFolderForm] = useState({ name: '', description: '' });
  const [notesForm, setNotesForm] = useState('');
  const [moveToFolder, setMoveToFolder] = useState('');

  // Add real-time subscription for saved jobs
  useRealtimeSubscriptions({
    savedJobs: true,
    onSavedJobUpdate: (data) => {
      if (data?.eventType === 'INSERT' || data?.eventType === 'UPDATE' || data?.eventType === 'DELETE') {
        // Refresh saved jobs list when changes occur
        loadSavedJobs();
      }
      
      if (data?.eventType === 'ALERT' && data?.alert) {
        // Show alert notification for saved job updates
        console.log('Job alert:', data?.alert?.message);
      }
    }
  });

  // Load saved jobs, folders, and alerts on mount
  useEffect(() => {
    if (user?.id) {
      loadAllData();
    }
  }, [user?.id]);

  /**
   * Load all saved jobs data
   */
  const loadAllData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [savedJobsResult, foldersResult, alertsResult] = await Promise.all([
        getSavedJobs(selectedFolder),
        getFolders(),
        getJobAlerts()
      ]);

      if (savedJobsResult?.success) {
        setSavedJobs(savedJobsResult?.data || []);
      } else {
        throw new Error(savedJobsResult?.error?.message);
      }

      if (foldersResult?.success) {
        setFolders(foldersResult?.data || []);
      }

      if (alertsResult?.success) {
        setAlerts(alertsResult?.data || []);
      }
    } catch (err) {
      console.error('Failed to load saved jobs data:', err);
      setError(err?.message || 'Failed to load saved jobs');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load saved jobs data (for real-time updates)
   */
  const loadSavedJobs = async () => {
    if (!user?.id) return;

    try {
      const result = await getSavedJobs(selectedFolder);
      
      if (result?.success) {
        setSavedJobs(result?.data || []);
      } else {
        throw new Error(result?.error?.message);
      }
    } catch (err) {
      console.error('Failed to load saved jobs:', err);
      setError(err?.message || 'Failed to load saved jobs');
    }
  };

  /**
   * Get job details from mock data
   */
  const getJobDetails = (jobId) => {
    const jobData = searchData?.find(item => item?.id === jobId && (!item?.type || item?.type === 'job'));
    return jobData || null;
  };

  /**
   * Handle unsaving a job
   */
  const handleUnsaveJob = async (jobId) => {
    if (!confirm('Remove this job from your saved list?')) return;

    try {
      const result = await unsaveJob(jobId);

      if (result?.success) {
        setSavedJobs(prev => prev?.filter(job => job?.jobId !== jobId));
      } else {
        throw new Error(result?.error?.message);
      }
    } catch (err) {
      console.error('Failed to unsave job:', err);
      alert(err?.message || 'Failed to remove job');
    }
  };

  /**
   * Handle creating a new folder
   */
  const handleCreateFolder = async () => {
    if (!folderForm?.name?.trim()) {
      alert('Please enter a folder name');
      return;
    }

    try {
      const result = await createFolder(folderForm?.name, folderForm?.description);

      if (result?.success) {
        setFolders(prev => [...prev, result?.data]);
        setFolderForm({ name: '', description: '' });
        setShowFolderModal(false);
      } else {
        throw new Error(result?.error?.message);
      }
    } catch (err) {
      console.error('Failed to create folder:', err);
      alert(err?.message || 'Failed to create folder');
    }
  };

  /**
   * Handle moving job to folder
   */
  const handleMoveToFolder = async () => {
    if (!selectedJob?.jobId) return;

    try {
      const result = await updateSavedJob(selectedJob?.jobId, {
        folderName: moveToFolder || null
      });

      if (result?.success) {
        setSavedJobs(prev =>
          prev?.map(job =>
            job?.jobId === selectedJob?.jobId
              ? { ...job, folderName: moveToFolder || null }
              : job
          )
        );
        setShowMoveModal(false);
        setSelectedJob(null);
        setMoveToFolder('');
      } else {
        throw new Error(result?.error?.message);
      }
    } catch (err) {
      console.error('Failed to move job:', err);
      alert(err?.message || 'Failed to move job');
    }
  };

  /**
   * Handle updating job notes
   */
  const handleUpdateNotes = async () => {
    if (!selectedJob?.jobId) return;

    try {
      const result = await updateSavedJob(selectedJob?.jobId, {
        notes: notesForm
      });

      if (result?.success) {
        setSavedJobs(prev =>
          prev?.map(job =>
            job?.jobId === selectedJob?.jobId
              ? { ...job, notes: notesForm }
              : job
          )
        );
        setShowEditNotesModal(false);
        setSelectedJob(null);
        setNotesForm('');
      } else {
        throw new Error(result?.error?.message);
      }
    } catch (err) {
      console.error('Failed to update notes:', err);
      alert(err?.message || 'Failed to update notes');
    }
  };

  /**
   * Handle deleting a folder
   */
  const handleDeleteFolder = async (folderName) => {
    if (!confirm(`Delete folder "${folderName}"? Jobs in this folder will be moved to "All Saved Jobs".`)) {
      return;
    }

    try {
      const result = await deleteFolder(folderName);

      if (result?.success) {
        setFolders(prev => prev?.filter(f => f?.folderName !== folderName));
        if (selectedFolder === folderName) {
          setSelectedFolder(null);
        }
        loadAllData();
      } else {
        throw new Error(result?.error?.message);
      }
    } catch (err) {
      console.error('Failed to delete folder:', err);
      alert(err?.message || 'Failed to delete folder');
    }
  };

  /**
   * Handle marking alert as read
   */
  const handleMarkAlertRead = async (alertId) => {
    try {
      const result = await markAlertAsRead(alertId);

      if (result?.success) {
        setAlerts(prev => prev?.filter(alert => alert?.id !== alertId));
      }
    } catch (err) {
      console.error('Failed to mark alert as read:', err);
    }
  };

  /**
   * Filter saved jobs based on search query
   */
  const filteredSavedJobs = savedJobs?.filter(savedJob => {
    const jobDetails = getJobDetails(savedJob?.jobId);
    if (!jobDetails) return false;

    const query = searchQuery?.toLowerCase();
    return (
      jobDetails?.title?.toLowerCase()?.includes(query) ||
      jobDetails?.companyName?.toLowerCase()?.includes(query) ||
      savedJob?.notes?.toLowerCase()?.includes(query)
    );
  });

  /**
   * Format job details for display
   */
  const formatSalary = (salary) => {
    if (!salary?.min || !salary?.max) return null;
    return `$${(salary?.min / 1000)?.toFixed(0)}k - $${(salary?.max / 1000)?.toFixed(0)}k`;
  };

  const formatLocation = (locations) => {
    if (!locations?.length) return 'Remote';
    const firstLoc = locations?.[0];
    return typeof firstLoc === 'string' ? firstLoc : `${firstLoc?.city}, ${firstLoc?.region}`;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <UnifiedCard className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
          </div>
        </UnifiedCard>
      </div>
    );
  }

  if (error) {
    return (
      <UnifiedCard>
        <div className="flex items-center gap-3 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <p>{error}</p>
        </div>
      </UnifiedCard>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <UnifiedCard>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className={`${typography?.h1} ${colors?.text?.primary} flex items-center gap-2`}>
              <BookmarkIcon className="h-6 w-6 text-accent" />
              Saved Jobs
            </h1>
            <p className={`${typography?.body?.small} ${colors?.text?.secondary} mt-1`}>
              {savedJobs?.length} {savedJobs?.length === 1 ? 'job' : 'jobs'} saved
            </p>
          </div>

          <div className="flex items-center gap-3">
            <UnifiedButton
              variant="secondary"
              size="small"
              onClick={() => setShowFolderModal(true)}
              className="flex items-center gap-2"
            >
              <FolderPlus className="h-4 w-4" />
              New Folder
            </UnifiedButton>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mt-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <SearchIcon className={`absolute left-3 top-3 h-4 w-4 ${colors?.text?.muted}`} />
            <UnifiedInput
              placeholder="Search saved jobs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="pl-10"
            />
          </div>
        </div>
      </UnifiedCard>

      {/* Job Alerts */}
      {alerts?.length > 0 && (
        <UnifiedCard>
          <div className="flex items-center gap-2 mb-4">
            <Bell className="h-5 w-5 text-accent" />
            <h2 className={`${typography?.h3} ${colors?.text?.primary}`}>Job Alerts</h2>
            <Chip size="sm" variant="accent">{alerts?.length}</Chip>
          </div>

          <div className="space-y-3">
            {alerts?.map((alert) => (
              <div
                key={alert?.id}
                className={`flex items-start justify-between p-3 ${colors?.background?.secondary} rounded-medium`}
              >
                <div className="flex-1">
                  <p className={`${typography?.body?.small} ${colors?.text?.primary} mb-1`}>
                    {alert?.message}
                  </p>
                  <p className={`${typography?.caption} ${colors?.text?.secondary}`}>
                    {new Date(alert?.createdAt)?.toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => handleMarkAlertRead(alert?.id)}
                  className={`${colors?.text?.secondary} hover:${colors?.text?.primary}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </UnifiedCard>
      )}

      {/* Folders Sidebar */}
      {folders?.length > 0 && (
        <UnifiedCard>
          <h2 className={`${typography?.h3} ${colors?.text?.primary} mb-4`}>Folders</h2>
          <div className="flex flex-wrap gap-2">
            <Chip
              variant={selectedFolder === null ? 'accent' : 'secondary'}
              onClick={() => setSelectedFolder(null)}
              className="cursor-pointer"
            >
              All Saved Jobs ({savedJobs?.length})
            </Chip>
            {folders?.map((folder) => (
              <div key={folder?.id} className="relative group">
                <Chip
                  variant={selectedFolder === folder?.folderName ? 'accent' : 'secondary'}
                  onClick={() => setSelectedFolder(folder?.folderName)}
                  className="cursor-pointer"
                >
                  <FolderIcon className="h-3 w-3 mr-1" />
                  {folder?.folderName} ({savedJobs?.filter(j => j?.folderName === folder?.folderName)?.length})
                </Chip>
                <button
                  onClick={() => handleDeleteFolder(folder?.folderName)}
                  className="absolute -top-1 -right-1 hidden group-hover:block bg-red-500 text-white rounded-full p-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </UnifiedCard>
      )}

      {/* Saved Jobs List */}
      <div className="space-y-4">
        {filteredSavedJobs?.length > 0 ? (
          filteredSavedJobs?.map((savedJob) => {
            const jobDetails = getJobDetails(savedJob?.jobId);
            if (!jobDetails) return null;

            return (
              <UnifiedCard key={savedJob?.id} hover className="cursor-pointer">
                <div className="flex justify-between items-start">
                  <div className="flex-1" onClick={() => navigate(`/jobs/${savedJob?.jobId}`)}>
                    <h3 className={`${typography?.h3} ${colors?.text?.primary} mb-2`}>
                      {jobDetails?.title}
                    </h3>

                    <div className={`flex items-center gap-4 ${typography?.body?.small} ${colors?.text?.secondary} mb-3`}>
                      <span className={`font-medium ${colors?.text?.primary}`}>
                        {jobDetails?.companyName}
                      </span>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {formatLocation(jobDetails?.locations)}
                      </div>
                      {formatSalary(jobDetails?.salary) && (
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4" />
                          {formatSalary(jobDetails?.salary)}
                        </div>
                      )}
                    </div>

                    {savedJob?.notes && (
                      <p className={`${typography?.body?.small} ${colors?.text?.secondary} mb-2`}>
                        📝 {savedJob?.notes}
                      </p>
                    )}

                    <div className="flex items-center gap-3">
                      {savedJob?.folderName && (
                        <Chip size="sm" variant="secondary">
                          <FolderIcon className="h-3 w-3 mr-1" />
                          {savedJob?.folderName}
                        </Chip>
                      )}
                      <span className={`${typography?.caption} ${colors?.text?.secondary}`}>
                        Saved {new Date(savedJob?.createdAt)?.toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <UnifiedButton
                      variant="ghost"
                      size="small"
                      onClick={(e) => {
                        e?.stopPropagation();
                        setSelectedJob(savedJob);
                        setNotesForm(savedJob?.notes || '');
                        setShowEditNotesModal(true);
                      }}
                      className="p-2"
                    >
                      <Edit2Icon className="h-4 w-4" />
                    </UnifiedButton>

                    <UnifiedButton
                      variant="ghost"
                      size="small"
                      onClick={(e) => {
                        e?.stopPropagation();
                        setSelectedJob(savedJob);
                        setMoveToFolder(savedJob?.folderName || '');
                        setShowMoveModal(true);
                      }}
                      className="p-2"
                    >
                      <MoveIcon className="h-4 w-4" />
                    </UnifiedButton>

                    <UnifiedButton
                      variant="ghost"
                      size="small"
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleUnsaveJob(savedJob?.jobId);
                      }}
                      className="p-2 text-red-500"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </UnifiedButton>
                  </div>
                </div>
              </UnifiedCard>
            );
          })
        ) : (
          <UnifiedCard className="text-center">
            <div className="max-w-md mx-auto">
              <BookmarkIcon className={`h-12 w-12 ${colors?.text?.muted} mx-auto mb-4`} />
              <h3 className={`${typography?.h3} ${colors?.text?.primary} mb-2`}>
                {searchQuery ? 'No matching saved jobs' : 'No saved jobs yet'}
              </h3>
              <p className={`${typography?.body?.default} ${colors?.text?.secondary} mb-4`}>
                {searchQuery
                  ? 'Try adjusting your search query' :'Start saving jobs to keep track of opportunities that interest you'}
              </p>
              {!searchQuery && (
                <UnifiedButton onClick={() => navigate('/dashboard/jobs')}>
                  Browse Jobs
                </UnifiedButton>
              )}
            </div>
          </UnifiedCard>
        )}
      </div>

      {/* Create Folder Modal */}
      {showFolderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <UnifiedCard className="w-full max-w-md mx-auto">
            <h3 className={`${typography?.h3} ${colors?.text?.primary} mb-4`}>Create Folder</h3>

            <div className="space-y-4">
              <div>
                <label className={`block ${typography?.label} ${colors?.text?.primary} mb-2`}>
                  Folder Name
                </label>
                <UnifiedInput
                  value={folderForm?.name}
                  onChange={(e) => setFolderForm({ ...folderForm, name: e?.target?.value })}
                  placeholder="e.g., Frontend Jobs"
                />
              </div>

              <div>
                <label className={`block ${typography?.label} ${colors?.text?.primary} mb-2`}>
                  Description (Optional)
                </label>
                <textarea
                  value={folderForm?.description}
                  onChange={(e) => setFolderForm({ ...folderForm, description: e?.target?.value })}
                  placeholder="Describe what jobs you'll save in this folder..."
                  rows={3}
                  className={`w-full px-4 py-3 ${colors?.background?.input} border ${colors?.border?.default} rounded-medium ${colors?.text?.primary} ${typography?.body?.small} focus:outline-none focus:ring-2 focus:ring-accent`}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <UnifiedButton
                variant="secondary"
                onClick={() => {
                  setShowFolderModal(false);
                  setFolderForm({ name: '', description: '' });
                }}
              >
                Cancel
              </UnifiedButton>
              <UnifiedButton variant="primary" onClick={handleCreateFolder}>
                Create Folder
              </UnifiedButton>
            </div>
          </UnifiedCard>
        </div>
      )}

      {/* Move to Folder Modal */}
      {showMoveModal && selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <UnifiedCard className="w-full max-w-md mx-auto">
            <h3 className={`${typography?.h3} ${colors?.text?.primary} mb-4`}>Move to Folder</h3>

            <div className="space-y-4">
              <div>
                <label className={`block ${typography?.label} ${colors?.text?.primary} mb-2`}>
                  Select Folder
                </label>
                <select
                  value={moveToFolder}
                  onChange={(e) => setMoveToFolder(e?.target?.value)}
                  className={`w-full px-4 py-3 ${colors?.background?.input} border ${colors?.border?.default} rounded-medium ${colors?.text?.primary} ${typography?.body?.small} focus:outline-none focus:ring-2 focus:ring-accent`}
                >
                  <option value="">No Folder</option>
                  {folders?.map((folder) => (
                    <option key={folder?.id} value={folder?.folderName}>
                      {folder?.folderName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <UnifiedButton
                variant="secondary"
                onClick={() => {
                  setShowMoveModal(false);
                  setSelectedJob(null);
                  setMoveToFolder('');
                }}
              >
                Cancel
              </UnifiedButton>
              <UnifiedButton variant="primary" onClick={handleMoveToFolder}>
                Move
              </UnifiedButton>
            </div>
          </UnifiedCard>
        </div>
      )}

      {/* Edit Notes Modal */}
      {showEditNotesModal && selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <UnifiedCard className="w-full max-w-md mx-auto">
            <h3 className={`${typography?.h3} ${colors?.text?.primary} mb-4`}>Edit Notes</h3>

            <div className="space-y-4">
              <div>
                <label className={`block ${typography?.label} ${colors?.text?.primary} mb-2`}>
                  Notes
                </label>
                <textarea
                  value={notesForm}
                  onChange={(e) => setNotesForm(e?.target?.value)}
                  placeholder="Add notes about this job..."
                  rows={4}
                  className={`w-full px-4 py-3 ${colors?.background?.input} border ${colors?.border?.default} rounded-medium ${colors?.text?.primary} ${typography?.body?.small} focus:outline-none focus:ring-2 focus:ring-accent`}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <UnifiedButton
                variant="secondary"
                onClick={() => {
                  setShowEditNotesModal(false);
                  setSelectedJob(null);
                  setNotesForm('');
                }}
              >
                Cancel
              </UnifiedButton>
              <UnifiedButton variant="primary" onClick={handleUpdateNotes}>
                Save Notes
              </UnifiedButton>
            </div>
          </UnifiedCard>
        </div>
      )}
    </div>
  );
};

export default SavedJobs;